import * as THREE from 'three';
import { terrainHeight } from './terrain';

// LIGHTNING: during a storm, an occasional bolt strikes the ground at a random world point (near or far),
// drawn as a jagged additive line that flickers out in ~0.2s, plus a brief GLOBAL white flash (read by the
// grade post) and a delayed thunder rumble (sound travels). Strike frequency scales with storm heaviness.
// Cosmetic, client-only -- no determinism needed for the bolts.

const MAXV = 90; // max bolt vertices (main channel + branches), as LINE SEGMENTS (point pairs)

export class LightningSystem {
  readonly group = new THREE.Group();
  flash = 0;                                       // 0..1 global flash, read by main -> grade.setFlash
  onStrike: ((dist: number, x: number, z: number) => void) | null = null; // thunder + intra-cloud-bloom hook (bolt distance + world XZ)
  private readonly bolt: THREE.LineSegments;
  private readonly pos: Float32Array;
  private boltLife = 0;                             // seconds the current bolt stays visible
  private cooldown = 0;                             // min gap until the next strike

  constructor() {
    this.pos = new Float32Array(MAXV * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);
    const mat = new THREE.LineBasicMaterial({ color: 0xeaf2ff, transparent: true, opacity: 0.95, depthTest: false, blending: THREE.AdditiveBlending });
    this.bolt = new THREE.LineSegments(geo, mat);
    this.bolt.frustumCulled = false;
    this.bolt.renderOrder = 6;
    this.bolt.visible = false;
    this.group.add(this.bolt);
  }

  update(dt: number, stormI: number, px: number, pz: number): void {
    this.flash = Math.max(0, this.flash - dt * 6.5);            // flash decays fast (~0.15s)
    if (this.boltLife > 0) {
      this.boltLife -= dt;
      (this.bolt.material as THREE.LineBasicMaterial).opacity = Math.min(1, this.boltLife * 8); // flicker out
      if (this.boltLife <= 0) this.bolt.visible = false;
    }
    this.cooldown -= dt;
    if (stormI > 0.45 && this.cooldown <= 0 && Math.random() < (stormI - 0.4) * dt * 0.9) {
      this.strike(px, pz, stormI);
      this.cooldown = 1.5 + Math.random() * 7;
    }
  }

  /** Force a bolt now (debug/testing). */
  forceStrike(px: number, pz: number): void { this.strike(px, pz, 1); }

  private strike(px: number, pz: number, stormI: number): void {
    const ang = Math.random() * Math.PI * 2;
    const dist = 25 + Math.random() * 165;                     // some close, some far
    const gx = px + Math.cos(ang) * dist, gz = pz + Math.sin(ang) * dist;
    const gy = terrainHeight(gx, gz);
    const topY = gy + 110 + Math.random() * 30;                // from up in the clouds
    let n = 0;
    const put = (x: number, y: number, z: number): void => { if (n < MAXV) { this.pos[n * 3] = x; this.pos[n * 3 + 1] = y; this.pos[n * 3 + 2] = z; n++; } };
    const segs = 11;
    let x = gx + (Math.random() - 0.5) * 6, y = topY, z = gz + (Math.random() - 0.5) * 6;
    for (let i = 0; i < segs; i++) {
      const t = (i + 1) / segs;
      const nx = gx + (Math.random() - 0.5) * (1 - t) * 14;
      const ny = topY + (gy - topY) * t;
      const nz = gz + (Math.random() - 0.5) * (1 - t) * 14;
      put(x, y, z); put(nx, ny, nz);                           // main-channel segment
      if (i > 2 && i < segs - 2 && Math.random() < 0.35) {     // a forking branch
        put(nx, ny, nz);
        put(nx + (Math.random() - 0.5) * 18, ny - 8 - Math.random() * 12, nz + (Math.random() - 0.5) * 18);
      }
      x = nx; y = ny; z = nz;
    }
    this.bolt.geometry.setDrawRange(0, n);
    (this.bolt.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.bolt.geometry.computeBoundingSphere();
    this.bolt.visible = true;
    this.boltLife = 0.16 + Math.random() * 0.1;
    const closeness = Math.max(0, 1 - dist / 180);
    this.flash = Math.min(0.78, 0.22 + closeness * 0.6) * (0.5 + 0.5 * stormI); // bright pulse, not a full white-out
    this.onStrike?.(dist, gx, gz);
  }
}
